import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AuthContainer } from "@/components/auth/AuthContainer";
import { AuthCard } from "@/components/auth/AuthCard";
import { PasswordResetForm } from "@/components/auth/form/PasswordResetForm";
import { CaptchaWidget } from "@/components/auth/CaptchaWidget";
import { useTurnstileSiteKey } from "@/hooks/useTurnstileSiteKey";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Send, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef<TurnstileInstance>(null);
  const turnstileSiteKey = useTurnstileSiteKey();
  const { language } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('access_token') && hash.includes('type=recovery')) {
      setIsRecoveryMode(true);
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecoveryMode(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handlePasswordResetComplete = async () => {
    setIsRecoveryMode(false);
    window.history.replaceState({}, document.title, window.location.pathname);
    await supabase.auth.signOut();
    navigate('/auth', { replace: true });
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      toast({
        title: language === 'ar' ? 'خطأ' : 'Error',
        description: language === 'ar' ? 'يرجى إدخال البريد الإلكتروني' : 'Please enter your email',
        variant: "destructive",
      });
      return;
    }

    if (!emailRegex.test(trimmedEmail)) {
      toast({
        title: language === 'ar' ? 'خطأ' : 'Error',
        description: language === 'ar' ? 'البريد الإلكتروني غير صحيح' : 'Invalid email format',
        variant: "destructive",
      });
      return;
    }

    if (turnstileSiteKey && !captchaToken) {
      toast({
        title: language === 'ar' ? 'خطأ' : 'Error',
        description: language === 'ar' ? 'يرجى إكمال التحقق من كابتشا' : 'Please complete the CAPTCHA challenge',
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
        captchaToken: captchaToken || undefined,
      });

      if (error) {
        toast({
          title: language === 'ar' ? 'خطأ' : 'Error',
          description: language === 'ar' 
            ? 'حدث خطأ أثناء إرسال رابط إعادة تعيين كلمة المرور'
            : (error.message || 'An error occurred while sending the password reset link'),
          variant: "destructive",
        });
        return;
      }

      setIsSuccess(true);
      toast({
        title: language === 'ar' ? 'تم الإرسال' : 'Email Sent',
        description: language === 'ar' 
          ? 'تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني'
          : 'Password reset link has been sent to your email',
      });
    } catch (error) {
      toast({
        title: language === 'ar' ? 'خطأ' : 'Error',
        description: language === 'ar' 
          ? 'حدث خطأ غير متوقع'
          : 'An unexpected error occurred',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      captchaRef.current?.reset();
      setCaptchaToken("");
    }
  };

  if (isRecoveryMode) {
    return (
      <AuthContainer>
        <AuthCard animateCard={false}>
          <PasswordResetForm onComplete={handlePasswordResetComplete} />
        </AuthCard>
      </AuthContainer>
    );
  }

  return (
    <AuthContainer>
      <AuthCard animateCard={false}>
        <div className="w-full space-y-6">
          {/* Back button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/auth')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {language === 'ar' ? 'العودة إلى تسجيل الدخول' : 'Back to login'}
          </Button>

          {/* Icon */}
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
            <Mail className="h-8 w-8 text-primary" />
          </div>

          {/* Title and description */}
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {language === 'ar' 
                ? 'أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة تعيين كلمة المرور'
                : 'Enter your email and we\'ll send you a password reset link'
              }
            </p>
          </div>

          {/* Form or success message */}
          {isSuccess ? (
            <div className="text-center space-y-4 py-8">
              <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Mail className="h-6 w-6 text-green-600" />
              </div>
              <div className="space-y-2">
                <h3 className="font-medium text-lg">
                  {language === 'ar' ? 'تحقق من بريدك الإلكتروني' : 'Check your email'}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {language === 'ar' 
                    ? 'تم إرسال رابط إعادة تعيين كلمة المرور إلى'
                    : 'We sent a password reset link to'
                  }
                </p>
                <p className="text-sm font-medium">{email}</p>
              </div>
              <Button
                onClick={() => navigate('/auth')}
                className="w-full"
              >
                {language === 'ar' ? 'العودة إلى تسجيل الدخول' : 'Back to login'}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">
                  {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                </Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                  required
                  disabled={isLoading}
                />
              </div>
              <CaptchaWidget ref={captchaRef} onVerify={setCaptchaToken} />
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading || !email}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    {language === 'ar' ? 'جاري الإرسال...' : 'Sending...'}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Send className="h-4 w-4" />
                    {language === 'ar' ? 'إرسال رابط إعادة التعيين' : 'Send Reset Link'}
                  </div>
                )}
              </Button>
            </form>
          )}
        </div>
      </AuthCard>
    </AuthContainer>
  );
}
